<template>
  <div class="courseRatings">
    <!-- Header -->
    <div class="courseRatings__header">
      <div class="courseRatings__header-content">
        <div class="courseRatings__header-icon">
          <v-icon color="white" size="28">mdi-star-circle</v-icon>
        </div>
        <div>
          <h1 class="courseRatings__header-title">Kursbewertungen</h1>
          <p class="courseRatings__header-subtitle">Bewertungen deiner Mitglieder im Überblick</p>
        </div>
      </div>

      <div class="courseRatings__header-actions">
        <v-btn color="primary" depressed rounded large class="courseRatings__studio-btn" @click="showStudioPicker">
          <v-icon left>mdi-store-outline</v-icon>
          Studio wechseln
        </v-btn>
      </div>
    </div>

    <!-- Date Picker -->
    <v-row no-gutters v-if="userService.hasRight('gf_r_viewAll')" class="mb-6">
      <v-col :cols="$vuetify.breakpoint.mobile ? 12 : 4" :md="4" :lg="3">
        <v-menu v-model="showDatePicker" :close-on-content-click="false" :nudge-right="0" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <div class="courseRatings__date-field" v-bind="attrs" v-on="on">
              <v-icon color="primary" class="mr-3">mdi-calendar-month</v-icon>
              <div class="courseRatings__date-field-content">
                <div class="courseRatings__date-field-label">Datum auswählen</div>
                <div class="courseRatings__date-field-value">
                  {{ formatDate(datePicker) || "Datum wählen" }}
                </div>
              </div>
              <v-icon color="grey lighten-1" class="ml-auto">mdi-chevron-down</v-icon>
            </div>
          </template>
          <v-date-picker v-model="datePicker" @input="showDatePicker = false" color="primary" first-day-of-week="1" locale="de-ch"></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>

    <!-- Ratings Grid -->
    <v-row v-if="ratings.length" class="courseRatings__grid">
      <v-col :cols="$vuetify.breakpoint.mobile ? 12 : 6" :md="6" :lg="4" v-for="rating of ratings" :key="rating._id">
        <v-card dark class="courseRatings__card">
          <!-- Card Header -->
          <div class="courseRatings__card-header">
            <div class="courseRatings__card-time">
              <v-icon size="16" color="white" class="mr-1">mdi-clock-outline</v-icon>
              {{ formatCourseTime(rating) }}
            </div>
            <h3 class="courseRatings__card-title">{{ rating.course.name }}</h3>
            <div class="courseRatings__card-instructors">
              <v-icon size="14" color="rgba(255,255,255,0.8)" class="mr-1"> mdi-account-multiple </v-icon>
              <span v-for="(instructor, idx) of rating.instructors" :key="instructor._id">
                {{ instructor.vorname }} {{ instructor.nachname }}<span v-if="idx < rating.instructors.length - 1">, </span>
              </span>
            </div>
            <div class="courseRatings__card-avg" v-if="averageRating(rating)">
              <v-rating
                background-color="rgba(255,255,255,0.3)"
                color="#ffd700"
                length="5"
                size="20"
                readonly
                dense
                half-increments
                :value="averageRating(rating)"></v-rating>
              <span class="courseRatings__card-avg-value">
                {{ averageRating(rating).toFixed(1) }}
              </span>
              <span class="courseRatings__card-avg-count"> ({{ rating.ratings.length }}) </span>
            </div>
          </div>

          <!-- Card Body: Individual ratings -->
          <div class="courseRatings__card-body">
            <div class="courseRatings__rating" v-for="r of rating.ratings" :key="r._id">
              <div class="courseRatings__rating-meta">
                <div class="courseRatings__rating-user">
                  {{ r.user.vorname }} {{ r.user.nachname }}

                  <div>
                    <div class="courseRatings__rating-name">{{ r.user.vorname }} {{ r.user.nachname }}</div>
                    <div class="courseRatings__rating-time">{{ formatRatingDate(r.ts) }} Uhr</div>
                  </div>
                </div>
                <v-rating background-color="#e0e0e0" color="#ffd700" length="5" size="18" readonly dense :value="r.stars"></v-rating>
              </div>

              <div dark v-if="r.message" class="courseRatings__rating-message">
                <v-icon size="14" color="grey lighten-1" class="mr-1"> mdi-format-quote-open </v-icon>
                {{ r.message }}
              </div>

              <div v-if="r.message" class="courseRatings__rating-actions">
                <v-btn text small color="primary" @click="copyToClipboard(r.message)">
                  <v-icon left size="16">mdi-content-copy</v-icon>
                  Kopieren
                </v-btn>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Empty State -->
    <div v-else class="courseRatings__empty">
      <div class="courseRatings__empty-icon">
        <v-icon size="64" color="grey lighten-2">mdi-star-outline</v-icon>
      </div>
      <h2 class="courseRatings__empty-title">Keine Bewertungen vorhanden</h2>
      <p class="courseRatings__empty-subtitle">An diesem Tag wurde noch kein Kurs bewertet.</p>
    </div>
  </div>
</template>

<script>
import api from "../../../api"
import userService from "../../../services/userService"

export default {
  name: "courseSettings",
  data() {
    return {
      userService,
      showCoursePictureList: false,
      datePicker: null,
      ratings: [],
      showDatePicker: false,
    }
  },
  async created() {
    this.datePicker = this.$moment().format("YYYY-MM-DD")
    await this.fetchRatings()
    this.$emit("finishedloading")
  },
  methods: {
    copyToClipboard(message) {
      navigator.clipboard.writeText(message)
      this.$toast.success("Bewertung kopiert")
    },
    showStudioPicker() {
      window.app.$root.$children[0].showStudioSelection = true
    },
    formatDate(date) {
      if (date === null) return ""
      let day = this.$moment(date).format("dd")
      return date.split("-")[2] + "." + date.split("-")[1] + "." + date.split("-")[0] + " (" + day + ")"
    },
    formatRatingDate(ts) {
      return this.$moment(ts * 1000).format("HH:mm")
    },
    formatCourseDate(course) {
      return (
        this.$moment(parseInt(course.ts) * 1000)
          .set({ hours: course.hours, minutes: course.minutes })
          .format("HH:mm") + " Uhr | "
      )
    },
    formatCourseTime(course) {
      return this.$moment(parseInt(course.ts) * 1000)
        .set({ hours: course.hours, minutes: course.minutes })
        .format("HH:mm")
    },
    initials(user) {
      const v = (user.vorname || "").trim()
      const n = (user.nachname || "").trim()
      return ((v[0] || "") + (n[0] || "")).toUpperCase() || "?"
    },
    averageRating(rating) {
      if (!rating.ratings || !rating.ratings.length) return 0
      const sum = rating.ratings.reduce((a, r) => a + (r.stars || 0), 0)
      return sum / rating.ratings.length
    },
    async fetchRatings() {
      let ratings = await api.fetchCourseRatings(this.$moment(this.datePicker).unix())
      this.ratings = ratings
    },
  },
  watch: {
    datePicker() {
      this.fetchRatings()
    },
  },
  computed: {},
}
</script>

<style lang="scss" scoped>
.courseRatings {
  padding: 16px 8px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 32px;
    padding: 24px;
    background: linear-gradient(
      135deg,
      rgba(var(--v-primary-base-rgb, 33, 150, 243), 0.12) 0%,
      rgba(var(--v-primary-base-rgb, 33, 150, 243), 0.04) 100%
    );
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 20px;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);

    &-content {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    &-icon {
      width: 52px;
      height: 52px;
      border-radius: 14px;
      background: var(--v-primary-base);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 8px 20px rgba(var(--v-primary-base-rgb, 33, 150, 243), 0.4);
    }

    &-title {
      font-size: 24px;
      font-weight: 700;
      color: white;
      margin: 0;
      line-height: 1.2;
    }

    &-subtitle {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.65);
      margin: 4px 0 0 0;
    }
  }

  &__studio-btn {
    text-transform: none;
    font-weight: 600;
    letter-spacing: 0;
  }

  &__date-field {
    display: flex;
    align-items: center;
    padding: 14px 20px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.2);
    }

    &-content {
      display: flex;
      flex-direction: column;
    }

    &-label {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.5);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-weight: 600;
    }

    &-value {
      font-size: 15px;
      color: white;
      font-weight: 600;
      margin-top: 2px;
    }
  }

  &__grid {
    margin: 0 -12px;
  }

  &__card {
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    height: 100%;
    display: flex;
    flex-direction: column;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    }

    &-header {
      background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-primary-darken1, #1976d2) 100%);
      color: white;
      padding: 20px 22px;
    }

    &-time {
      display: inline-flex;
      align-items: center;
      font-size: 12px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
      background: rgba(255, 255, 255, 0.15);
      padding: 4px 10px;
      border-radius: 999px;
      margin-bottom: 10px;
    }

    &-title {
      font-size: 20px;
      font-weight: 700;
      margin: 0 0 8px 0;
      color: white;
      line-height: 1.3;
    }

    &-instructors {
      display: flex;
      align-items: center;
      font-size: 13px;
      color: rgba(255, 255, 255, 0.85);
    }

    &-avg {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: 14px;
      padding-top: 14px;
      border-top: 1px solid rgba(255, 255, 255, 0.2);

      &-value {
        font-size: 16px;
        font-weight: 700;
        color: white;
      }

      &-count {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.7);
      }
    }

    &-body {
      padding: 8px 0;
      flex: 1;
    }
  }

  &__rating {
    padding: 16px 22px;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }

    &-meta {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 10px;
    }

    &-user {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
    }

    &-avatar {
      width: 38px;
      height: 38px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-primary-darken1, #1976d2) 100%);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      font-weight: 700;
      flex-shrink: 0;
    }

    &-name {
      font-size: 14px;
      font-weight: 600;
      color: #1a1a1a;
      line-height: 1.2;
    }

    &-time {
      font-size: 12px;
      color: #888;
      margin-top: 2px;
    }

    &-message {
      border-left: 3px solid var(--v-primary-base);
      border-radius: 8px;
      padding: 12px 14px;
      font-size: 14px;

      line-height: 1.5;
      margin-top: 8px;
    }

    &-actions {
      display: flex;
      justify-content: flex-end;
      margin-top: 6px;
    }
  }

  &__empty {
    text-align: center;
    padding: 80px 20px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px dashed rgba(255, 255, 255, 0.15);
    border-radius: 20px;

    &-icon {
      margin-bottom: 16px;
      opacity: 0.6;
    }

    &-title {
      font-size: 20px;
      font-weight: 700;
      color: white;
      margin: 0 0 8px 0;
    }

    &-subtitle {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.6);
      margin: 0;
    }
  }
}

@media (max-width: 600px) {
  .courseRatings {
    padding: 8px 4px;

    &__header {
      padding: 20px;
      flex-direction: column;
      align-items: flex-start;

      &-title {
        font-size: 20px;
      }
    }

    &__studio-btn {
      width: 100%;
    }

    &__card-header {
      padding: 18px;
    }

    &__rating {
      padding: 14px 18px;
    }
  }
}
</style>
